<template>
  <div class="app-container">
    <!-- 内容包裹层 - 统一控制宽度和缩放 -->
    <div class="content-wrapper">

      <!-- ✅ 新增：自定义 Toast 消息提示组件 -->
      <div v-if="toast.show" class="toast-container" :class="toast.type">
        <div class="toast-content">
          <span class="toast-icon">{{ toast.icon }}</span>
          <span class="toast-message">{{ toast.message }}</span>
          <router-link
            v-if="toast.actionPath"
            :to="toast.actionPath"
            class="toast-action"
            @click.native="closeToast"
          >
            {{ toast.actionLabel }}
          </router-link>
        </div>
      </div>

      <!-- 提交进度遮罩 -->
      <div v-if="loading.show" class="loading-overlay">
        <div class="loading-spinner"></div>
        <div class="loading-text">{{ loading.text }}</div>
        <div v-if="loading.total > 0" class="loading-progress-bar">
          <div class="loading-progress-fill" :style="{ width: `${loadingPercent}%` }"></div>
        </div>
        <div v-if="loading.total > 0" class="loading-progress-text">
          {{ loading.current }} / {{ loading.total }}
        </div>
      </div>

      <!-- ✅ 新增：自定义确认对话框组件 -->
      <div v-if="confirmDialog.show" class="confirm-dialog-overlay" @click.self="handleConfirmCancel">
        <div class="confirm-dialog">
          <div class="confirm-dialog-header">
            <span class="confirm-dialog-icon">⚠️</span>
            <span class="confirm-dialog-title">确认操作</span>
          </div>
          <div class="confirm-dialog-body">
            <p class="confirm-dialog-message" v-html="confirmDialog.message"></p>
          </div>
          <div class="confirm-dialog-footer">
            <button class="confirm-btn cancel-btn" @click="handleConfirmCancel">取消</button>
            <button class="confirm-btn confirm-btn-primary" @click="handleConfirmSubmit">确认</button>
          </div>
        </div>
      </div>

      <!-- 视频下载区域 -->
      <div class="video-downloader">
        <div class="header-row">
          <h1 class="title">视频下载</h1>
        </div>

        <!-- 滑动提示（仅小屏幕显示）-->
        <div class="scroll-hint" v-if="isMobile">
          <i class="fas fa-arrows-alt-h"></i>
          <span>左右滑动查看完整内容</span>
        </div>

        <!-- ✅ 修改：仅保留 Bilibili 图标 -->
        <div class="platform-icons">
          <div class="platform">
            <i class="fab fa-bilibili platform-icon bilibili-icon"></i>
            <span>Bilibili</span>
          </div>
        </div>

        <div class="table-container">
          <div class="upload-section">
            <div class="upload-button">
              <label for="upload-txt" class="upload-label">
                <i class="fas fa-file-upload"></i>
                上传 TXT 批量导入
              </label>
              <input type="file" id="upload-txt" accept=".txt" @change="handleFileUpload" style="display:none" />
            </div>
          </div>

          <div class="table-section">
            <table class="download-table">
              <thead>
                <tr>
                  <th class="sequence-column">序号</th>
                  <th class="url-column">视频 URL</th>
                  <th class="status-column">状态</th>
                  <th class="format-column">下载格式</th>
                  <th class="platform-column">视频平台</th>
                  <th class="action-column">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(task, index) in downloadTasks" :key="task.id || index">
                  <td class="sequence-column">{{ index + 1 }}</td>
                  <td class="url-column">
                    <input
                      type="text"
                      v-model="task.url"
                      @input="resetTaskStatus(task)"
                      placeholder="请输入 Bilibili 视频链接"
                      class="url-input"
                      :class="{ 'input-error': task.url && !isValidBilibiliUrl(task.url) }"
                      @blur="validateTaskUrl(index)"
                      title="仅支持 Bilibili 视频链接，如：https://www.bilibili.com/video/BV1xx411c7mD"
                    />
                    <!-- ✅ 新增：输入框下方显示校验提示 -->
                    <div v-if="task.url && !isValidBilibiliUrl(task.url)" class="url-error-tip">
                      <i class="fas fa-exclamation-circle"></i> 请输入有效的 bilibili.com 链接（该行未计入费用）
                    </div>
                  </td>
                  <td class="status-column">
                    <span :class="['task-status', `task-status-${getTaskStatus(task)}`]">
                      {{ getTaskStatusText(task) }}
                    </span>
                  </td>
                  <!-- ✅ 格式固定为 MP4 -->
                  <td class="format-column">
                    <span class="fixed-option">MP4</span>
                  </td>
                  <!-- ✅ 平台固定为 Bilibili -->
                  <td class="platform-column">
                    <span class="fixed-option">Bilibili</span>
                  </td>
                  <td class="action-column">
                    <button @click="removeTask(index)" class="delete-button" aria-label="删除任务" title="删除任务">
                      <i class="fas fa-trash-alt" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="table-actions">
            <button @click="addTask" class="add-task-button">
              <i class="fas fa-plus"></i> 添加任务
            </button>

            <div v-if="validTasks.length > 0 || invalidTaskCount > 0" class="cost-summary">
              <span v-if="validTasks.length > 0">
                共 {{ validTasks.length }} 个有效任务，费用：¥{{ totalCost }}（余额：¥{{ formattedBalance }}）
              </span>
              <span v-if="invalidTaskCount > 0" class="invalid-cost-hint">
                另有 {{ invalidTaskCount }} 行格式错误，未计入费用
              </span>
            </div>

            <div class="action-buttons">
              <button
                v-if="failedTaskCount > 0"
                class="retry-failed-button"
                @click="retryFailedTasks"
              >
                重试失败项
              </button>
              <button
                @click="startDownload"
                class="download-button"
                :disabled="!hasValidTasks"
                :title="!hasValidTasks ? '请至少填写一个有效的 Bilibili 视频链接' : ''"
              >
                开始全部下载
              </button>
            </div>
          </div>

          <div v-if="failureSummary.length > 0" class="failure-summary">
            <div class="failure-summary-title">失败任务明细</div>
            <div v-for="(item, index) in failureSummary" :key="index" class="failure-item">
              <span class="failure-url">{{ item.url }}</span>
              <span class="failure-reason">{{ item.error }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { videosDownapi, getUserInfo } from '@/api/upfile'
import { mapGetters } from 'vuex'

export default {
  name: 'VideoDownloader',
  data () {
    return {
      // ✅ 修改：默认平台改为 bilibili（小写，与后端接口一致）
      downloadTasks: [
        { url: '', format: 'MP4', platform: 'bilibili', id: Date.now() + '-1', status: 'idle', error: '' },
        { url: '', format: 'MP4', platform: 'bilibili', id: Date.now() + '-2', status: 'idle', error: '' }
      ],
      isMobile: false,
      // 单价配置（便于后续调整）
      pricePerTask: 2.0,
      loading: {
        show: false,
        text: '',
        current: 0,
        total: 0
      },
      failureSummary: [],
      // ✅ 新增：Toast 消息提示状态
      toast: {
        show: false,
        message: '',
        type: 'info', // success | error | warning | info
        icon: 'ℹ️',
        timer: null,
        actionLabel: '',
        actionPath: ''
      },
      // ✅ 新增：确认对话框状态
      confirmDialog: {
        show: false,
        message: '',
        resolve: null,
        reject: null
      }
    }
  },
  computed: {
    ...mapGetters(['balance', 'formattedBalance', 'isLoggedIn']),
    filledTasks () {
      return this.downloadTasks.filter(task => task.url.trim() !== '')
    },
    validTasks () {
      return this.filledTasks.filter(task => this.isValidBilibiliUrl(task.url))
    },
    invalidTaskCount () {
      return this.filledTasks.length - this.validTasks.length
    },
    failedTaskCount () {
      return this.downloadTasks.filter(task => task.status === 'failed').length
    },
    totalCost () {
      return parseFloat((this.validTasks.length * this.pricePerTask).toFixed(2))
    },
    hasValidTasks () {
      return this.validTasks.length > 0
    },
    // ✅ 新增：余额是否充足计算属性
    isBalanceSufficient () {
      return this.balance >= this.totalCost
    },
    loadingPercent () {
      if (!this.loading.total) return 0
      return Math.min(100, Math.round((this.loading.current / this.loading.total) * 100))
    }
  },
  mounted () {
    this.loadFontAwesome()
    this.checkMobile()
    // 初始化时刷新用户余额
    this.refreshBalance()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkMobile)
    // ✅ 清理 Toast 定时器，防止内存泄漏
    if (this.toast.timer) {
      clearTimeout(this.toast.timer)
    }
    // ✅ 清理确认对话框回调，防止内存泄漏
    if (this.confirmDialog.resolve) {
      this.confirmDialog.resolve(false)
      this.confirmDialog.resolve = null
      this.confirmDialog.reject = null
    }
  },
  methods: {
    checkMobile () {
      this.isMobile = window.innerWidth < 768
    },
    loadFontAwesome () {
      const existingLink = document.querySelector('link[href*="font-awesome"]')
      if (!existingLink) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css'
        link.crossOrigin = 'anonymous'
        link.referrerPolicy = 'no-referrer'
        document.head.appendChild(link)
      }
    },
    addTask () {
      // ✅ 修改：新增任务默认平台为 bilibili
      this.downloadTasks.push({
        url: '',
        format: 'MP4',
        platform: 'bilibili',
        id: Date.now() + '-' + Math.random().toString(36).substr(2, 9),
        status: 'idle',
        error: ''
      })
    },
    removeTask (index) {
      this.downloadTasks.splice(index, 1)
    },
    // ✅ 新增：校验单个任务 URL 并显示提示
    validateTaskUrl (index) {
      const task = this.downloadTasks[index]
      if (!task.url) {
        task.status = 'idle'
        task.error = ''
      } else if (this.isValidBilibiliUrl(task.url)) {
        task.status = 'pending'
        task.error = ''
      } else {
        task.status = 'invalid'
        task.error = '格式错误'
      }
    },
    resetTaskStatus (task) {
      task.status = task.url && this.isValidBilibiliUrl(task.url) ? 'pending' : 'idle'
      task.error = ''
    },
    // ✅ 核心简化：验证 Bilibili 链接 - 只要包含 bilibili.com 域名即合法
    isValidBilibiliUrl (url) {
      if (!url || typeof url !== 'string') {
        return false
      }

      const trimmedUrl = url.trim().toLowerCase()

      // ✅ 简化逻辑：只要链接包含 bilibili.com 域名即视为合法
      // 支持各种协议和子域名变体：
      // - https://www.bilibili.com/xxx
      // - http://www.bilibili.com/xxx
      // - https://bilibili.com/xxx
      // - //www.bilibili.com/xxx
      // - www.bilibili.com/xxx
      // - bilibili.com/xxx
      // - https://xxx.bilibili.com/xxx (子域名)

      // 1. 尝试补全协议后解析
      let fullUrl = trimmedUrl
      if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
        if (trimmedUrl.startsWith('//')) {
          fullUrl = 'https:' + trimmedUrl
        } else {
          fullUrl = 'https://' + trimmedUrl
        }
      }

      try {
        const urlObj = new URL(fullUrl)
        const hostname = urlObj.hostname.toLowerCase()

        // ✅ 核心校验：域名必须包含 bilibili.com
        return hostname.includes('bilibili.com')
      } catch (e) {
        // URL 解析失败，尝试简单字符串匹配作为降级方案
        return trimmedUrl.includes('bilibili.com')
      }
    },
    // ✅ 批量校验所有任务链接
    validateAllTasks () {
      const invalidTasks = []

      this.filledTasks.forEach((task, index) => {
        if (!this.isValidBilibiliUrl(task.url)) {
          invalidTasks.push({
            index: index + 1,
            url: task.url
          })
        }
      })

      return {
        isValid: invalidTasks.length === 0,
        invalidTasks
      }
    },
    handleFileUpload (event) {
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = async e => {
        const content = String(e.target.result || '').replace(/^\uFEFF/, '')
        const rawLines = content
          .split(/\r?\n/)
          .map(line => line.trim())
          .filter(line => line && !line.startsWith('#'))
        const rawCount = rawLines.length

        const uniqueUrls = []
        const seenUrls = new Set()
        rawLines.forEach(url => {
          const normalized = url.toLowerCase()
          if (!seenUrls.has(normalized)) {
            seenUrls.add(normalized)
            uniqueUrls.push(url)
          }
        })

        if (uniqueUrls.length === 0) {
          this.showToast('warning', '⚠️', '文件中未找到有效链接')
          return
        }

        const hasExistingTasks = this.downloadTasks.some(task => task.url.trim() !== '')
        let shouldReplace = false

        if (hasExistingTasks) {
          const confirmMsg = `发现 <strong>${rawCount}</strong> 条链接，去重后 <strong>${uniqueUrls.length}</strong> 条。<br/>确认替换当前任务列表？取消则追加。`
          try {
            shouldReplace = await this.showConfirmDialog(confirmMsg)
          } catch (error) {
            return
          }
        } else {
          this.downloadTasks = []
        }

        if (shouldReplace) {
          this.downloadTasks = []
        }

        // ✅ 修改：导入任务时默认平台为 bilibili
        uniqueUrls.forEach((url, i) => {
          this.downloadTasks.push({
            url,
            format: 'MP4',
            platform: 'bilibili',
            id: Date.now() + '-' + i + '-' + Math.random().toString(36).substr(2, 6),
            status: 'idle',
            error: ''
          })
        })

        const invalidCount = uniqueUrls.filter(url => !this.isValidBilibiliUrl(url)).length
        event.target.value = ''
        const message = `成功导入 ${uniqueUrls.length} 个链接`
        if (invalidCount > 0) {
          this.showToast('warning', '⚠️', `${message}，其中 ${invalidCount} 个链接格式不正确，请检查红色行`)
        } else {
          this.showToast('success', '✅', message)
        }
      }
      reader.readAsText(file)
    },
    // ✅ 核心修改：startDownload 方法 - 添加 URL 合法性校验
    async startDownload () {
      // 1. 登录状态检查
      if (!this.isLoggedIn) {
        this.showToast('error', '❌', '请先登录')
        return
      }

      // 2. 有效任务检查
      if (!this.hasValidTasks) {
        this.showToast('error', '❌', '请至少添加一个有效的下载任务')
        return
      }

      // 3. 存在格式错误行时阻止提交，避免费用统计歧义
      if (this.invalidTaskCount > 0) {
        this.showToast('error', '❌', `还有 ${this.invalidTaskCount} 行链接格式不正确，请修正后再提交`)
        return
      }

      // 4. 余额充足性检查
      if (!this.isBalanceSufficient) {
        const deficit = (this.totalCost - this.balance).toFixed(2)
        this.showToast('error', '❌', `余额不足！当前余额 ¥${this.balance}，需要 ¥${this.totalCost}，还差 ¥${deficit}`)
        return
      }

      // 5. 使用自定义确认对话框替代原生 confirm
      const confirmMsg = `确认提交 <strong>${this.validTasks.length}</strong> 个下载任务？<br/>费用：<strong>¥${this.totalCost}</strong><br/>剩余余额：<strong>¥${(this.balance - this.totalCost).toFixed(2)}</strong>`

      try {
        const confirmed = await this.showConfirmDialog(confirmMsg)
        if (!confirmed) {
          // 用户点击取消
          return
        }
      } catch (error) {
        // 对话框被强制关闭等异常情况
        console.error('确认对话框异常:', error)
        return
      }

      // 6. 提交并处理结果
      await this.submitTasks(this.validTasks)
    },
    async submitTasks (tasks) {
      this.failureSummary = []
      this.loading = {
        show: true,
        text: '正在提交下载任务...',
        current: 0,
        total: tasks.length
      }

      try {
        const result = await this.processDownloadTasks(tasks)
        this.handleSubmissionResult(result, tasks.length)
      } catch (error) {
        console.error('下载过程中出错:', error)
        this.showToast('error', '❌', '下载失败，请重试：' + (error.message || '未知错误'))
      } finally {
        this.loading.show = false
      }
    },
    handleSubmissionResult (result, attemptedCount) {
      const failedResults = result.results.filter(item => !item.success)
      this.failureSummary = failedResults.map(item => ({
        url: item.task.url,
        error: item.error || '未知错误'
      }))

      const viewRecordsAction = {
        label: '查看操作记录',
        path: '/recode'
      }

      if (result.successCount === attemptedCount && attemptedCount > 0) {
        this.downloadTasks = this.createEmptyTasks()
        this.showToast('success', '✅', `已提交 ${attemptedCount} 个任务`, 5000, viewRecordsAction)
      } else if (result.successCount === 0) {
        this.showToast('error', '❌', `全部 ${failedResults.length} 个任务提交失败`, 6000)
      } else {
        this.showToast('warning', '⚠️', `成功 ${result.successCount} 个，失败 ${failedResults.length} 个`, 6000, viewRecordsAction)
      }

      this.refreshBalance()
    },
    async retryFailedTasks () {
      const failedTasks = this.downloadTasks.filter(task => task.status === 'failed')
      if (failedTasks.length === 0) return

      if (!this.isBalanceSufficient) {
        const cost = failedTasks.length * this.pricePerTask
        const deficit = (cost - this.balance).toFixed(2)
        this.showToast('error', '❌', `余额不足，重试需要 ¥${cost}，还差 ¥${deficit}`)
        return
      }

      await this.submitTasks(failedTasks)
    },
    createEmptyTasks () {
      return [
        { url: '', format: 'MP4', platform: 'bilibili', id: Date.now() + '-1', status: 'idle', error: '' },
        { url: '', format: 'MP4', platform: 'bilibili', id: Date.now() + '-2', status: 'idle', error: '' }
      ]
    },
    getTaskStatus (task) {
      if (!task.url.trim()) return 'idle'
      if (!this.isValidBilibiliUrl(task.url)) return 'invalid'
      return task.status || 'pending'
    },
    getTaskStatusText (task) {
      const statusMap = {
        idle: '未填写',
        invalid: '格式错误',
        pending: '待提交',
        submitting: '提交中',
        success: '已提交',
        failed: '提交失败'
      }
      return statusMap[this.getTaskStatus(task)] || '未知'
    },
    // ✅ 新增：自定义 Toast 消息提示方法
    showToast (type, icon, message, duration = 3000, action = null) {
      // 清理之前的定时器
      if (this.toast.timer) {
        clearTimeout(this.toast.timer)
      }
      // 更新 Toast 状态
      this.toast = {
        show: true,
        type,
        icon,
        message,
        actionLabel: action ? action.label : '',
        actionPath: action ? action.path : ''
      }
      // 设置自动关闭定时器
      this.toast.timer = setTimeout(() => {
        this.toast.show = false
        this.toast.timer = null
      }, duration)
    },
    closeToast () {
      if (this.toast.timer) {
        clearTimeout(this.toast.timer)
      }
      this.toast.show = false
      this.toast.actionLabel = ''
      this.toast.actionPath = ''
    },
    // ✅ 新增：显示自定义确认对话框（返回 Promise）
    showConfirmDialog (message) {
      return new Promise((resolve, reject) => {
        // 保存回调函数
        this.confirmDialog = {
          show: true,
          message,
          resolve,
          reject
        }
      })
    },
    // ✅ 处理确认对话框 - 用户点击确认
    handleConfirmSubmit () {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(true)
      }
      this.closeConfirmDialog()
    },
    // ✅ 处理确认对话框 - 用户点击取消或遮罩层
    handleConfirmCancel () {
      if (this.confirmDialog.resolve) {
        this.confirmDialog.resolve(false)
      }
      this.closeConfirmDialog()
    },
    // ✅ 关闭确认对话框
    closeConfirmDialog () {
      this.confirmDialog.show = false
      this.confirmDialog.message = ''
      this.confirmDialog.resolve = null
      this.confirmDialog.reject = null
    },
    async processDownloadTasks (tasks) {
      const results = []

      for (const [index, task] of tasks.entries()) {
        task.status = 'submitting'
        task.error = ''
        this.loading.current = index + 1

        try {
          const data = {
            input_file_url: task.url,
            operation_type: 'videoDownload',
            video_platform: task.platform
          }
          const processResponse = await videosDownapi(data)
          if (processResponse.status === 200) {
            // 实时更新余额（如果接口返回）
            if (typeof processResponse.balance !== 'undefined') {
              this.$store.commit('setBalance', processResponse.balance)
            }
            task.status = 'success'
            results.push({ success: true, task, error: '' })
          } else {
            const message = processResponse.message || '提交失败'
            task.status = 'failed'
            task.error = message
            results.push({ success: false, task, error: message })
          }
        } catch (error) {
          console.error(`任务 ${index + 1}/${tasks.length} 提交失败:`, error)
          const message = error.message || '网络错误'
          task.status = 'failed'
          task.error = message
          results.push({ success: false, task, error: message })
        }
      }

      // 任务处理完成后刷新余额
      await this.refreshBalance()

      return {
        successCount: results.filter(item => item.success).length,
        failCount: results.filter(item => !item.success).length,
        results
      }
    },
    async refreshBalance () {
      try {
        const response = await getUserInfo()
        if (response?.data?.account_balance !== undefined) {
          this.$store.commit('setBalance', parseFloat(response.data.account_balance))
        }
      } catch (error) {
        console.error('刷新余额失败:', error)
      }
    }
  }
}
</script>

<style scoped>
/* ========== 全局容器 ========== */
.app-container {
  min-height: calc(100vh - 150px);
  padding: 20px;
  margin-top: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  box-sizing: border-box;
  background-color: #1a1a1a;
  position: relative;
}

/* ========== 内容包裹层 ========== */
.content-wrapper {
  width: 100%;
  max-width: 800px;
  min-height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
}

@media (min-width: 800px) {
  .content-wrapper {
    max-width: 800px;
  }
}

@media (max-width: 800px) {
  .app-container {
    overflow-x: auto;
    background-color: #1a1a1a;
  }
}

/* ========== 视频下载区域 ========== */
.video-downloader {
  width: 100%;
  height: auto;
  padding: 10px 20px 20px;
  font-family: Arial, sans-serif;
  background-color: #2d2d2d;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
}

.header-row {
  position: relative;
  margin-bottom: 10px;
  min-height: 40px;
}

.title {
  margin: 0;
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  text-align: center;
  width: 100%;
}

/* ========== 滑动提示 ========== */
.scroll-hint {
  display: none;
  text-align: center;
  padding: 8px;
  margin-bottom: 10px;
  background: linear-gradient(90deg, transparent, rgba(74, 158, 255, 0.15), transparent);
  border-radius: 4px;
  color: #4a9eff;
  font-size: 13px;
  animation: pulse 2s infinite;
}

.scroll-hint i {
  margin-right: 6px;
  animation: slideArrow 1.5s ease-in-out infinite;
}

@keyframes slideArrow {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(8px); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

@media (max-width: 768px) {
  .scroll-hint { display: block; }
}

.platform-icons {
  display: flex;
  justify-content: center;
  margin-bottom: 5px;
  gap: 40px;
}

.platform {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #b0b0b0;
}

.platform-icon {
  width: 24px;
  height: 24px;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bilibili-icon {
  color: #00A1D6;
}

.table-container {
  position: relative;
  min-height: 0;
}

.upload-section {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 6px;
}

.upload-label {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #3a3a3a;
  border-radius: 4px;
  cursor: pointer;
  color: #4a9eff;
  font-size: 14px;
  border: 1px solid #4a4a4a;
  transition: background-color 0.3s;
}

.upload-label:hover {
  background-color: #454545;
}

.upload-label i {
  color: #4a9eff;
  margin-right: 5px;
}

.table-section {
  height: 310px;
  overflow-x: auto;
  overflow-y: auto;
  margin-bottom: 10px;
  border: 2px solid #080808;
  -webkit-overflow-scrolling: touch;
}

.table-section::-webkit-scrollbar,
.app-container::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-section::-webkit-scrollbar-track,
.app-container::-webkit-scrollbar-track {
  background: #2d2d2d;
  border-radius: 4px;
}

.table-section::-webkit-scrollbar-thumb,
.app-container::-webkit-scrollbar-thumb {
  background: #4a4a4a;
  border-radius: 4px;
}

.table-section::-webkit-scrollbar-thumb:hover,
.app-container::-webkit-scrollbar-thumb:hover {
  background: #555555;
}

.download-table {
  width: 100%;
  min-width: 820px;
  border-collapse: collapse;
  table-layout: fixed;
}

.download-table th,
.download-table td {
  border: 1px solid #3a3a3a;
  padding: 10px;
  text-align: left;
  color: #e0e0e0;
  word-wrap: break-word;
  word-break: break-all;
}

.download-table th {
  background-color: #353535;
  font-weight: bold;
  position: sticky;
  top: 0;
  z-index: 1;
  text-align: center;
}

.sequence-column {
  width: 60px;
  min-width: 60px;
  text-align: center;
  vertical-align: middle;
  padding: 10px;
  font-weight: bold;
}

.url-column {
  width: auto;
  min-width: 200px;
}

.status-column {
  width: 105px;
  min-width: 105px;
  text-align: center;
}

.format-column {
  width: 90px;
  min-width: 90px;
  text-align: center;
}

.platform-column {
  width: 100px;
  min-width: 100px;
  text-align: center;
}

.action-column {
  width: 80px;
  min-width: 80px;
  text-align: center;
  vertical-align: middle;
  padding: 10px;
}

.url-input {
  width: 100%;
  min-width: 180px;
  padding: 8px;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.url-input::placeholder {
  color: #888888;
}

.url-input:focus {
  outline: none;
  border-color: #4a9eff;
  background-color: #404040;
}

/* ✅ 新增：URL 输入框错误状态样式 */
.url-input.input-error {
  border-color: #ef4444 !important;
  background-color: #4a2d2d !important;
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

/* ✅ 新增：URL 错误提示文本样式 */
.url-error-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 4px;
}

.url-error-tip i {
  font-size: 11px;
}

.delete-button {
  background-color: transparent;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  transition: color 0.3s;
}

.delete-button:hover {
  color: #ff5252;
}

.cost-summary {
  text-align: center;
  margin: 0 10px;
  font-size: 13px;
  color: #4a9eff;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.invalid-cost-hint {
  color: #ff6b6b;
  margin-left: 10px;
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}

.add-task-button {
  padding: 4px 6px;
  background-color: #3a3a3a;
  border: 1px solid #4a4a4a;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #4a9eff;
  transition: background-color 0.3s;
  white-space: nowrap;
}

.add-task-button:hover {
  background-color: #454545;
}

.download-button {
  padding: 4px 6px;
  background-color: #4a9eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.3s;
  white-space: nowrap;
}

.download-button:hover {
  background-color: #3a8eef;
}

.download-button:disabled {
  background-color: #555;
  cursor: not-allowed;
  opacity: 0.7;
}

.retry-failed-button {
  padding: 4px 10px;
  background-color: #f59e0b;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  white-space: nowrap;
}

.retry-failed-button:hover {
  background-color: #d97706;
}

.fixed-option {
  display: inline-block;
  padding: 6px 10px;
  border-radius: 4px;
  background-color: #2a2a2a;
  color: #4a9eff;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.task-status {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.task-status-idle { color: #888888; }
.task-status-invalid { color: #ef4444; background-color: #4a2d2d; }
.task-status-pending { color: #4a9eff; }
.task-status-submitting { color: #f59e0b; }
.task-status-success { color: #10b981; }
.task-status-failed { color: #ef4444; background-color: #4a2d2d; }

.failure-summary {
  margin-top: 14px;
  padding: 12px;
  background-color: #2a2d2d;
  border: 1px solid #4a2d2d;
  border-radius: 6px;
}

.failure-summary-title {
  color: #ff6b6b;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
}

.failure-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  border-bottom: 1px solid #3a3a3a;
  color: #e0e0e0;
  font-size: 13px;
}

.failure-item:last-child {
  border-bottom: none;
}

.failure-url {
  word-break: break-all;
}

.failure-reason {
  color: #ff6b6b;
}

/* ========== 全局加载遮罩 ========== */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  border: 4px solid #3a3a3a;
  border-top: 4px solid #4a9eff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
  margin-bottom: 10px;
}

.loading-text {
  color: white;
  font-size: 16px;
}

.loading-progress-bar {
  width: 220px;
  height: 6px;
  margin-top: 14px;
  background-color: #3a3a3a;
  border-radius: 3px;
  overflow: hidden;
}

.loading-progress-fill {
  height: 100%;
  background-color: #4a9eff;
  transition: width 0.2s ease;
}

.loading-progress-text {
  margin-top: 8px;
  color: #b0b0b0;
  font-size: 13px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== ✅ 新增：Toast 消息提示样式 ========== */
.toast-container {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: slideDown 0.3s ease-out;
  min-width: 280px;
  max-width: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-container.success {
  background-color: #10b981;
  color: white;
}

.toast-container.error {
  background-color: #ef4444;
  color: white;
}

.toast-container.warning {
  background-color: #f59e0b;
  color: white;
}

.toast-container.info {
  background-color: #3b82f6;
  color: white;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
}

.toast-icon {
  font-size: 18px;
}

.toast-message {
  flex: 1;
  word-break: break-word;
  white-space: pre-line; /* ✅ 支持换行显示 */
}

.toast-action {
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 5px;
  background-color: rgba(255, 255, 255, 0.18);
  color: white;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.toast-action:hover {
  background-color: rgba(255, 255, 255, 0.32);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* 移动端适配 Toast */
@media (max-width: 768px) {
  .toast-container {
    width: calc(100% - 40px);
    min-width: auto;
    max-width: calc(100% - 40px);
  }
}

/* ========== ✅ 新增：确认对话框样式 ========== */
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;
}

.confirm-dialog {
  background-color: #2d2d2d;
  border: 1px solid #3a3a3a;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  width: 90%;
  max-width: 420px;
  overflow: hidden;
  animation: dialogSlideIn 0.3s ease-out;
}

.confirm-dialog-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #3a3a3a;
  background-color: #353535;
}

.confirm-dialog-icon {
  font-size: 20px;
}

.confirm-dialog-title {
  font-size: 16px;
  font-weight: bold;
  color: #ffffff;
}

.confirm-dialog-body {
  padding: 20px;
}

.confirm-dialog-message {
  margin: 0;
  font-size: 14px;
  color: #e0e0e0;
  line-height: 1.6;
  text-align: center;
}

.confirm-dialog-message strong {
  color: #4a9eff;
  font-weight: 600;
}

.confirm-dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #3a3a3a;
  background-color: #353535;
}

.confirm-btn {
  padding: 8px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.confirm-btn.cancel-btn {
  background-color: #3a3a3a;
  color: #e0e0e0;
  border: 1px solid #4a4a4a;
}

.confirm-btn.cancel-btn:hover {
  background-color: #454545;
  border-color: #5a5a5a;
}

.confirm-btn.confirm-btn-primary {
  background-color: #4a9eff;
  color: white;
}

.confirm-btn.confirm-btn-primary:hover {
  background-color: #3a8eef;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* 移动端适配确认对话框 */
@media (max-width: 768px) {
  .confirm-dialog {
    width: calc(100% - 40px);
    max-width: calc(100% - 40px);
  }

  .confirm-dialog-footer {
    flex-direction: column-reverse;
    gap: 10px;
  }

  .confirm-btn {
    width: 100%;
  }
}
</style>
